/**
 * Isolated external store for the LIVE Bark Battle scoreboard (Challenge game #2), driven by the
 * server's BARK_TICK at ~10 Hz while a battle window is open.
 *
 * WHY this exists (same lesson as mic-level.store, perf-audit R1): routing the 10 Hz score ticks
 * through OfficePage state → HUD props would reconcile the ENTIRE un-memoized HUD subtree ten times
 * a second for the whole 10-second window, stealing main-thread time from the Pixi render loop.
 * This tiny pub/sub is consumed by EXACTLY one leaf (the battle card's scoreboard), so the ticks
 * repaint only the two numbers + the tug bar. The slow-changing match identity (matchId / phase /
 * opponent) stays ordinary React state.
 *
 * Deliberately a bare pub/sub, not Zustand: one writer (the BARK_TICK callback in OfficePage), one
 * reader (BarkScoreboard), no derived state. Values are display-only — the SERVER owns the totals.
 */

export interface BarkScoreSnapshot {
  myScore: number;
  theirScore: number;
  /** ms left in the barking window (server clock; display-only). */
  remainingMs: number;
}

let snapshot: BarkScoreSnapshot = { myScore: 0, theirScore: 0, remainingMs: 0 };
const listeners = new Set<() => void>();

function publish(next: BarkScoreSnapshot): void {
  snapshot = next;
  for (const notify of listeners) notify();
}

/** Publish the latest authoritative totals (a BARK_TICK arrived). */
export function setBarkScore(myScore: number, theirScore: number, remainingMs: number): void {
  if (
    myScore === snapshot.myScore &&
    theirScore === snapshot.theirScore &&
    remainingMs === snapshot.remainingMs
  ) {
    return;
  }
  publish({ myScore, theirScore, remainingMs });
}

/** Reset for a fresh battle (countdown start) so the card never flashes the previous match's totals. */
export function resetBarkScore(durationMs: number): void {
  publish({ myScore: 0, theirScore: 0, remainingMs: durationMs });
}

/** Current snapshot — the `getSnapshot` for useSyncExternalStore. */
export function getBarkScore(): BarkScoreSnapshot {
  return snapshot;
}

/** Subscribe to score changes — the `subscribe` for useSyncExternalStore. Returns an unsubscribe. */
export function subscribeBarkScore(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}
